import { getSessionTag } from "@/utils/sessionTag";

class ReportApi {
  constructor(vm, context) {
    this.$backend = vm.$backend;
    this.context = context;
    if (this.context.scope == "mislaka") {
      this.root = `/mislaka/request/${this.context.reportId}`;
    } else {
      this.root = `/client/${this.context.clientId}/report/${this.context.reportId}`;
    }
  }

  async loadReportAndScope() {
    const [info, scope, report, reportScope] = await Promise.all([
      this.loadInfo(),
      this.loadScope(),
      this.loadReport(),
      this.loadReportScope()
    ]);
    return {
      info,
      scope,
      report,
      reportScope
    };
  }

  async loadInfo() {
    return await this.$backend.getJson(this.root + "/info");
  }

  async loadInfoValues() {
    return await this.$backend.getJson(this.root + "/info-values");
  }

  async updateInfo(newInfo) {
    return await this.$backend.postJson(this.root + "/info", newInfo);
  }

  async loadScope() {
    return await this.$backend.getJson(this.root + "/scope");
  }

  async loadReport(mode) {
    if (mode == "reset") {
      return await this.$backend.postJson(this.root, {});
    } else if (mode == "invalidate") {
      return await this.$backend.getJson(this.root + "/invalidate");
    } else {
      return await this.$backend.getJson(this.root);
    }
  }

  async loadReportScope() {
    return await this.$backend.getJson(this.root + "/report-scope");
  }

  invalidate() {
    return this.$backend.getJson(this.root + "/invalidate");
  }

  explain() {
    return this.$backend.getJson(this.root + "/explain");
  }

  analyze(options) {
    return this.$backend.getJson(this.root + "/analyze", {
      params: options
    });
  }

  content() {
    return this.$backend.getJson(this.root + "/content");
  }

  loadPolisot(state = "before") {
    return this.$backend.getJson(this.root + "/" + state);
  }

  sanitizeReport(report) {
    //remove fixes suggestions from polisot
    let json = JSON.stringify(report, (key, value) => {
      if (key == "fixes") return undefined;
      if (key == "changesSummary") return undefined;
      return value;
    });
    return JSON.parse(json);
  }

  async updateReport(newReport, newForms) {
    return await this.$backend.postJson(this.root, {
      report: this.sanitizeReport(newReport),
      forms: newForms
    });
  }

  async archiveReport(archive) {
    return await this.$backend.postJson(this.root + "/archive", {
      archive
    });
  }

  deleteReport() {
    return this.$backend.delete(this.root);
  }

  duplicateReport(newReport, options = {}) {
    return this.$backend.postJson(this.root + "/duplicate", newReport, {
      params: options
    });
  }

  async suggestReportCode() {
    return await this.$backend.postJson(this.root + "/suggest-report-code", {});
  }

  async recalculateReport(newReport) {
    return await this.$backend.postJson(
      this.root + "/recalculate",
      newReport
    );
  }

  async recalculateReportWithFixes(newReport) {
    let reportWithFixes = await this.$backend.postJson(
      this.root + "/recalculate?fixes=true",
      //this.root + "/recalculate",
      newReport
    );
    return reportWithFixes;
  }

  analyzeReport(report, options) {
    return this.$backend.postJson(this.root + "/analyze", report, {
      params: options
    });
  }

  getPerformance(report, month, calculate_fv_in_years = 5) {
    return this.$backend.postJson(this.root + "/performance", {
      report,
      month,
      calculate_fv_in_years
    });
  }

  async optimizeReport(report, field, riskLevel, month, limits, calculate_fv_in_years) {
    const body = { report, riskLevel, month, limits };
    if (Array.isArray(field)) {
      body.fields = field;
    } else {
      body.field = field;
    }
    if (calculate_fv_in_years !== undefined) {
      body.calculate_fv_in_years = calculate_fv_in_years;
    }
    let { optimizedReport, optimizationForMutzar } = await this.$backend.postJson(this.root + "/optimize", body);
    return { optimizedReport, optimizationForMutzar };
  }

  async optimizeSkylineReport(report, field, month, options) {
    let body = { report, month, use_market_to_fill_history: true };
    if (Array.isArray(field)) {
      body.fields = field;
    } else {
      body.field = field;
    }
    if (options) {
      if (options.mixRatio !== undefined) {
        body.mixRatio = options.mixRatio;
      }
      if (options.hevrot) {
        body.hevrot = options.hevrot;
      }
      if (options.split_to_hevort !== undefined) {
        body.split_to_hevort = options.split_to_hevort;
      }
    } 
    let results = await this.$backend.postJson(this.root + "/optimize-skyline", body);
    return results;
  }

  async getInvestmentSettings(report) {
    return await this.$backend.postJson(this.root + "/investment_settings", { report });
  }

  async loadForms() {
    return await this.$backend.getJson(this.root + "/forms");
  }
 

  async generateForms(forms, onUpdate, externalAdvisorId) {
    let jobId = await this.$backend.postJson("/job", {
      job: "generate-forms",
      inputs: { 
        clientId: this.context.clientId,
        reportId: this.context.reportId,
        ui: `${window.location.protocol}//${window.location.host}`,
        forms,
        externalAdvisorId
      }
    });
    let status = await this.$backend.getJson("/job/" + jobId + "/status");
    onUpdate(status);
    while (!status.done) {
      status = await this.$backend.getJson("/job/" + jobId + "/status");
      onUpdate(status);
    }
    return this.$backend.getJson("/job/" + jobId);
  }

  isClientPublished() {
    return this.$backend.getJson(this.root + "/is-published", {});
  }

  async publish(people, packageId) {
    return await this.$backend.postJson(this.root + "/publish", {
      people,
      packageId
    });
  }

  async sendSignPackage(person, method, title) {
    return await this.$backend.postJson(this.root + "/send-sign-package", {
      person,
      method,
      title
    });
  }

  async getFile(file) {
    let response = await this.$backend.get(this.root + "/file/" + file);
    return await response.text();
  }

  hasChanges() {
    return this.$backend.getJson(this.root + "/changes");
  }

  recalculateReportWithChanges(report) {
    return this.$backend.postJson(this.root + "/changes?fixes=true", { report });
  }

  compareReport(report, referenceId) {
    return this.$backend.postJson(this.root + "/changes", { report, referenceId });
  }

  getInfoChanges() {
    return this.$backend.getJson(this.root + "/info-changes");
  }

  getReportMissingFields(report, referenceReport) {
    return this.$backend.postJson(this.root + "/find-missing-fields", { report, referenceReport});
  }

  getMislakaProcess() {
    return this.$backend.getJson(this.root + "/mislaka-process");
  }
}

class ClientApi {
  constructor(vm, context) {
    this.vm = vm;
    this.$backend = vm.$backend;
    this.context = context;
    this.root = `/client/${this.context.clientId}`;
  }

  async load() {
    let response = await this.$backend.getJson(this.root);
    if (response.newReports && response.newReports.length > 0) {
      response = await this.refreshReport(response.newReports);
    }
    return {
      info: response.info,
      lastReport: response.lastReport,
      reports: response.reports
    };
  }

  refreshReport(reports) {
    return this.$backend.postJson(this.root + "/refresh", {
      reports
    });
  }

  loadInfo() {
    return this.$backend.getJson(this.root + "/info");
  }

  loadAccount() {
    return this.$backend.getJson(this.root + "/account");
  }

  loadLoginHistory() {
    return this.$backend.getJson(this.root + "/login-history");
  }

  unlockClient() {
    return this.$backend.postJson(this.root + "/login-history", { locked: false });
  }

  report(id) {
    return new ReportApi(
      this.vm,
      { scope: "client", clientId: this.context.clientId, reportId: id }
    );
  }

  deleteReport(id) {
    return this.report(id)
      .deleteReport()
      .then(() => {
        return this.load();
      });
  }

  duplicateReport(id, newReport, options) {
    return this.report(id)
      .duplicateReport(newReport, options)
      .then((report) => {
        return this.load().then(() => {
          return report;
        });
      });
  }

  getPublishedReport() {
    return this.$backend.getJson(this.root + "/publishedReport");
  }

  async setPublishedReport(id) {
    const response = await this.$backend.postJson(this.root + "/publishedReport", { reportId: id });
    return {
      info: response.info,
      lastReport: response.lastReport,
      reports: response.reports
    };
  }

  updateAccount(account) {
    return this.$backend.postJson(this.root + "/account", account);
  }

  getReportCodes() {
    return this.$backend.getJson(this.root + "/report-codes");
  }

  async syncToDuckDb(onUpdate) {
    let jobId = await this.$backend.postJson(this.root + "/sync-to-duckdb", {});
    let status = await this.$backend.getJson("/job/" + jobId + "/status");
    onUpdate(status);
    while (!status.done) {
      status = await this.$backend.getJson("/job/" + jobId + "/status");
      onUpdate(status);
    }
    return this.$backend.getJson("/job/" + jobId);
  }

  loadInvites() {
    return this.$backend.getJson(this.root + "/invite");
  }

  createInvite() {
    return this.$backend.postJson(this.root + "/invite", {});
  }

  resendInvite(inviteId) {
    return this.$backend.postJson(
      this.root + "/invite/" + inviteId + "/resend",
      {}
    );
  }

  deleteInvite(inviteId) {
    return this.$backend.deleteJson(this.root + "/invite/" + inviteId, {});
  }

  startGetDataRequest() {
    return this.$backend.postJson(this.root + "/request-data", {});
  }

  loadLastEmails() {
    return this.$backend.getJson(this.root + "/audit/emails");
  }

  loadMessageThreads() {
    return this.$backend.getJson(this.root + "/messages");
  }

  postMessage(thread, message, markRead) {
    return this.$backend.postJson(this.root + "/messages", {
      threadId: thread.id,
      title: thread.title,
      message,
      markRead
    });
  }

  markRead(thread, message) {
    return this.$backend.postJson(this.root + "/messages/read", {
      threadId: thread.id,
      messageId: message.id
    });
  }

  uploadYiupiKoach(hevrot, idFile, b1File, taarich_chtima_lakoach, taarich_chtima_baal_rishaion) {
    let formData = new FormData();
    formData.append("hevrot", hevrot);
    if (taarich_chtima_lakoach) {
      formData.append("taarich_chtima_lakoach", taarich_chtima_lakoach);
    }
    if (taarich_chtima_baal_rishaion) {
      formData.append("taarich_chtima_baal_rishaion", taarich_chtima_baal_rishaion);
    }
    formData.append("id_file", idFile);
    formData.append("b1_fill_file", b1File);
    return this.$backend.postJson(this.root + "/yiupi-koach", formData);
  }
}

export default {
  install(Vue /* options */) {
    const actions = {};
    Vue.prototype.$actions = actions;

    const actionVm = new Vue({});

    actions.mislaka = {
      stats() {
        return actionVm.$backend.getJson("/mislaka/stats");
      },
      upload(input) {
        let fileToUpload = input.files[0];
        let formData = new FormData();
        formData.append("file", fileToUpload);
        return actionVm.$backend.postJson("/mislaka/request", formData);
      },
      getDownloadedFile(file) {
        return actionVm.$backend.getJson("/mislaka/downloaded/" + file);
      },
      file(id) {
        if (!(id in actionVm.$store.filesById)) {
          actionVm.$store.filesById[id] = {
            id: id,
            info: undefined,
            scope: undefined,
            report: undefined,
            reportScope: undefined
          };
        }
        return new ReportApi(
          actionVm,
          { scope: "mislaka", reportId: id }
        );
      },
      import(id) {
        return actionVm.$backend.postJson("/mislaka/import/" + id, {});
      },
      mergeIntoExistingReport(id, targetReportId) {
        return actionVm.$backend.postJson(
          "/mislaka/request/" + id + "/merge-into-existing-report",
          { targetReportId }
        );
      },
      requestFileProcess() {
        return actionVm.$backend.postJson("/mislaka/request-file-process", {});
      },
      updateTags(request) {
        return actionVm.$backend.postJson("/mislaka/request/updateTags", request);
      },
      archiveYipuiKoach(folder) {
        return actionVm.$backend.postJson("/mislaka/request/archive-yipui-koach", {
          folder
        });
      }
    };

    actions.clients = {
      loadClients(options) {
        const views = (options && Array.isArray(options.views))
          ? [...new Set(options.views.filter((view) => !!view))]
          : [];
        const params = views.length ? { views: views.join(",") } : {};
        return actionVm.$backend.getJson("/client", { params }).then((clients) => {
          let ids = {};
          actionVm.$store.clients = clients.map((client) => {
            let obj = {
              id: client.id,
              info: client.info,
              published_report_taarich_nechonut: client.published_report_taarich_nechonut,
              published_report_total_saving: client.published_report_total_saving,
              published_report_total_349_saving: client.published_report_total_349_saving,
              published_report_date: client.published_report_date,
              account: client.account,
              syncStatus: client.sync_status,
              latest_report_id: client.latest_report_id,
              latest_taarich_nechonut: client.latest_taarich_nechonut,
              scope: undefined,
              reports: undefined,
              report: undefined
            };
            ids[client.id] = obj;
            return obj;
          });
          actionVm.$store.clientsById = ids;
          actionVm.$store.clientsLoaded = true;
          actionVm.$store.clientsLoadedViews = views;
          return actionVm.$store.clients;
        });
      },
      donwloadSummary(month, onUpdate) {
        return actions.doJob('client-summary', { month }, onUpdate).then((response)=>{
          if (response.ok) {
            return response.blob();
          }
          throw new Error("fail to download summary");
        });
      },
      loadActiveClients() {
        return actionVm.$backend.getJson("/client/active");
      },
      searchClientsWithProducts(products) {
        return actionVm.$backend.getJson("/client/search", {
          params: products
        });
      },
      create(info) {
        return actionVm.$backend.postJson("/client/", { info });
      },
      client(id) {
        if (!(id in actionVm.$store.clientsById)) {
          actionVm.$store.clientsById[id] = {
            id: id,
            info: undefined,
            lastReport: undefined,
            syncStatus: 'waiting',
            scope: undefined,
            reports: undefined,
            report: undefined
          };
        }
        return new ClientApi(
          actionVm,
          { scope: "client", clientId: id },
          actionVm.$store.clientsById[id]
        );
      }
    };

    actions.currentReport = {
      async load() {
        return actionVm.$backend.getJson("/account/report/current");
      },
      async info() {
        return actionVm.$backend.getJson("/account/report/current/info");
      },
      async analyze(options) {
        return actionVm.$backend.getJson("/account/report/current/analyze", {
          params: options
        });
      },
      async explain() {
        return actionVm.$backend.getJson("/account/report/current/explain");
      },
      async content() {
        return actionVm.$backend.getJson("/account/report/current/content");
      },
      async lastUpdated() {
        return actionVm.$backend.getJson("/account/report/current/last-updated");
      },
      async forms() {
        return actionVm.$backend.getJson("/account/report/current/forms");
      },
      async updateForms(forms) {
        return actionVm.$backend.postJSon(
          "/account/report/current/forms",
          forms
        );
      },
      getPerformance(report, month, calculate_fv_in_years = 5) {
        return actionVm.$backend.postJson("/account/report/current/performance", {
          report,
          month,
          calculate_fv_in_years
        });
      }
    };

    actions.messages = {
      load() {
        return actionVm.$backend.getJson("/account/messages");
      },
      loadUnread() {
        return actionVm.$backend.getJson("/account/messages/unread");
      },
      postMessage(thread, message) {
        return actionVm.$backend.postJson("/account/messages", {
          threadId: thread.id,
          title: thread.title,
          message
        });
      },
      markRead(thread, message) {
        return actionVm.$backend.postJson("/account/messages/read", {
          threadId: thread.id,
          messageId: message.id
        });
      }
    };

    actions.getRankingDateRange = function () {
      return actionVm.$backend.getJson(`/mutzar/date-range`);
    };

    actions.updateRanking = function () {
      return actionVm.$backend.postJson(`/mutzar/update`);
    };

    actions.loadMarketPerformance = function (month) {
      return actionVm.$backend.getJson(`/mutzar/market-performance?month=${month}`);
    };

    actions.loadMarketPerformanceRange = function (from, to) {
      return actionVm.$backend.getJson(`/mutzar/market-performance/range?from=${from}&to=${to}`);
    };

    actions.marketPlaygroundPerformance = function (params) {
      return actionVm.$backend.postJson('/mutzar/market-playground/performance', params);
    };

    actions.loadMaslulim = function (sugMutzar, month) {
      if (
        actionVm.$store.maslulimDate[sugMutzar] == month &&
        actionVm.$store.maslulim[sugMutzar]
      ) {
        return Promise.resolve(actionVm.$store.maslulim[sugMutzar]);
      }

      return actionVm.$backend
        .getJson(`/mutzar/${sugMutzar}/maslul?month=${month}`)
        .then((newMaslulim) => {
          Vue.set(actionVm.$store.maslulim, sugMutzar, newMaslulim);
          Vue.set(actionVm.$store.maslulimDate, sugMutzar, month);
          return actionVm.$store.maslulim[sugMutzar];
        });
    };

    actions.getKupaDetails = function (sugMutzar, misparMaslul, month) {
      return actionVm.$backend.getJson(`/mutzar/${sugMutzar}/maslul/${misparMaslul}?month=${month}`);
    };

    actions.loadMaslulimRanking = function (sugMutzar, category, month) {
      return actionVm.$backend.getJson(
        `/mutzar/${sugMutzar}/rank/${category}?month=${month}`
      );
    };

    actions.loadRowRanking = function (row, threshold, months, fullDetails) {
      if (threshold) {
        return actionVm.$backend.postJson(
          `/mutzar/${row.sug_mutzar}/rank/byRamatSikun?threshold=${threshold}&months=${months}`,
          row
        );
      } else {
        const requestOptions = fullDetails
          ? { params: { full_details: true } }
          : undefined;
        return actionVm.$backend.postJson(
          "/mutzar/" + row.sug_mutzar + "/rank/",
          row,
          requestOptions
        );
      }
    };

    actions.loadBituachMaslulim = function (sugKisui) {
      return actionVm.$backend
        .getJson("/bituach/" + sugKisui + "/maslul")
        .then((bituchim) => {
          Vue.set(actionVm.$store.bituachMaslulim, sugKisui, bituchim);
          return actionVm.$store.bituachMaslulim[sugKisui];
        });
    };

    actions.getRequiredFormsForRow = function (row, info, polisot, externalAdvisorId) {
      return actionVm.$backend.postJson("/forms/requiredForms", {
        row,
        info,
        polisot,
        externalAdvisorId
      });
    };

    actions.getSpecificFormsForRow = function (rules, row, info, polisot, externalAdvisorId) {
      return actionVm.$backend.postJson("/forms/fillForms", {
        rules,
        row,
        info,
        polisot,
        externalAdvisorId
      });
    };

    actions.getAvailableFieldsForRow = function (row, info, polisot, externalAdvisorId) {
      return actionVm.$backend.postJson("/forms/availableFields", {
        row,
        info,
        polisot,
        externalAdvisorId
      });
    };

    actions.inspectForms = function (forms) {
      return actionVm.$backend.postJson("/forms/inspectForms", { forms });
    };

    actions.refillExternalAdvisorFields = function (forms, row, info, polisot, externalAdvisorId) {
      return actionVm.$backend.postJson("/forms/refillExternalAdvisorFields", {
        forms,
        row,
        info,
        polisot,
        externalAdvisorId
      });
    };

    actions.loadForms = function () {
      return actionVm.$backend.getJson("/forms/").then((formsByCategory) => {
        actionVm.$store.formsByCategory = formsByCategory;
        actionVm.$store.forms = [].concat(
          ...Object.values(actionVm.$store.formsByCategory).map(
            (category) => category.forms
          )
        );
        return actionVm.$store.forms;
      });
    };

    actions.loadFormsRules = function () {
      return actionVm.$backend.getJson("/forms/rules").then((rules) => {
        rules.forEach((rule) => {
          rule.checked = false;
        });
        actionVm.$store.formsRules = rules;
        return actionVm.$store.formsRules;
      });
    };

    actions.saveFormRule = function (rule) {
      return actionVm.$backend
        .postJson("/forms/rules", { rule })
        .then((newRule) => {
          let foundItem = actionVm.$store.formsRules.filter(
            (i) => i.id == newRule.id
          )[0];
          if (foundItem) {
            let index = actionVm.$store.formsRules.indexOf(foundItem);
            actionVm.$store.formsRules.splice(index, 1, newRule);
          } else {
            actionVm.$store.formsRules.push(newRule);
          }
          return actionVm.$store.formsRules;
        });
    };

    actions.deleteFormRule = function (rule) {
      return actionVm.$backend
        .deleteJson("/forms/rules/" + rule.id)
        .then((deletedRule) => {
          let foundItem = actionVm.$store.formsRules.filter(
            (i) => i.id == deletedRule.id
          )[0];
          if (foundItem) {
            let index = actionVm.$store.formsRules.indexOf(foundItem);
            actionVm.$store.formsRules.splice(index, 1);
          }
          return actionVm.$store.formsRules;
        });
    };

    actions.downloadEmptyForm = function (formId, clientId, reportId) {
      const tag = getSessionTag();
      return actionVm.$backend
        .get("/forms/" + formId, {
          params: { clientId, reportId, tag }
        })
        .then((response) => {
          if (response.ok) {
            return response.blob();
          }
          throw new Error("fail to download form");
        });
    };

    actions.completeForm = function (
      formId,
      fields,
      signatures,
      clientId,
      reportId,
      flatten,
      onUpdate
    ) {
      const filename = String(formId || "form.pdf").split("/").pop();
      return actions.completeSeveralForms(
        [{
          form: formId,
          filename,
          fields: fields || {},
          signatures,
          repo: clientId && reportId ? "report" : undefined
        }],
        clientId,
        reportId,
        flatten,
        onUpdate
      );
    };

    actions.doJob = async function (job, inputs, onUpdate) {
      let jobId = await actionVm.$backend.postJson("/job", {
        job,
        inputs
      });
      let status = await actionVm.$backend.getJson("/job/" + jobId + "/status");
      if (onUpdate) onUpdate(status);
      while (!status.done) {
        status = await actionVm.$backend.getJson("/job/" + jobId + "/status");
        if (onUpdate) onUpdate(status);
      }
      return actionVm.$backend.get("/job/" + jobId);
    }

    actions.completeSeveralForms = async function (forms, clientId, reportId, flatten, onUpdate) {
      let lastStatus = { progress: 0, logs: [] };
      if (onUpdate) onUpdate(lastStatus);
      return actions.doJob("zip-forms", { forms, clientId, reportId, flatten }, (status) => {
        lastStatus = status || lastStatus;
        if (onUpdate) onUpdate(lastStatus);
      }).then((response) => {
        if (response.ok) {
          return response.blob();
        }
        const error = new Error("fail to download forms");
        error.logs = lastStatus.logs || [];
        throw error;
      });
    };

    function sanitizeInfo(info) {
      let fixedInfo = { ...info };
      delete fixedInfo.title;
      delete fixedInfo.description;

      return fixedInfo;
    }

    actions.recalculateRowWithFixes = function (row, info, type) {
      switch (type) {
        case "polisa":
          return actionVm.$backend.postJson("/row", {
            row,
            info: sanitizeInfo(info)
          });
        case "bituach":
          return actionVm.$backend.postJson("/bituach_row", {
            row,
            info: sanitizeInfo(info)
          });
        case "halvaa":
          return Promise.resolve({ row });
        default:
          throw new Error("unknown row type " + type);
      }
    };

    actions.recalculateRow = function (row, info, type) {
      return actions
        .recalculateRowWithFixes(row, info, type)
        .then((result) => result.row);
    };

    actions.simulateRow = function (row, info, type, monthDelta = 0) {
      switch (type) {
        case "polisa":
          return actionVm.$backend.postJson("/row/simulate", {
            row,
            info: sanitizeInfo(info),
            delta: monthDelta
          });
        case "bituach":
          return actionVm.$backend.postJson("/bituach_row/simulate", {
            row,
            info: sanitizeInfo(info),
            delta: monthDelta
          });
        case "halvaa":
          throw new Error("Can't simulate halvaa");
        default:
          throw new Error("unknown row type " + type);
      }
    };

    actions.loadSavedItems = function () {
      return actionVm.$backend.getJson("/templates/").then((savedItems) => {
        actionVm.$store.savedItems = savedItems;
        return actionVm.$store.savedItems;
      });
    };

    actions.saveItem = function (name, item, type) {
      return actionVm.$backend
        .postJson("/templates/", { name, item, type })
        .then((savedItem) => {
          let foundItem = actionVm.$store.savedItems.filter(
            (i) => i.name == name
          )[0];
          if (foundItem) {
            foundItem.item = savedItem.item;
            foundItem.type = savedItem.type;
          } else {
            foundItem = savedItem;
            actionVm.$store.savedItems.push(savedItem);
          }
          return foundItem;
        });
    };

    actions.removeSavedItem = function (name) {
      let foundItem = actionVm.$store.savedItems.filter(
        (savedItem) => savedItem.name == name
      )[0];
      if (foundItem) {
        let index = actionVm.$store.savedItems.indexOf(foundItem);
        actionVm.$store.savedItems.splice(index, 1);
        return actionVm.$backend.delete(
          "/templates/" + encodeURIComponent(name)
        );
      } else {
        return Promise.resolve(null);
      }
    };

    actions.loadFilters = function () {
      return actionVm.$backend.getJson("/filters/store").then((filters) => {
        actionVm.$store.filters = filters;
        return actionVm.$store.filters;
      });
    };

    actions.loadFiltersGroups = function () {
      return actionVm.$backend.getJson("/filters/groups");
    };

    actions.saveFilter = function (filter) {
      return actionVm.$backend
        .postJson("/filters/store", filter)
        .then((newFilter) => {
          actionVm.$set(actionVm.$store.filters, newFilter.name, newFilter);
          return newFilter;
        });
    };

    actions.removeFilter = function (filter) {
      return actionVm.$backend
        .delete("/filters/store/" + filter.name)
        .then((/* response */) => {
          actionVm.$delete(actionVm.$store.filters, filter.name);
          return filter;
        });
    };

    actions.testFilter = function (filter) {
      return actionVm.$backend.postJson("/filters/match/", filter);
    };

    actions.loadHanmakaLines = function () {
      return actionVm.$backend
        .getJson("/hanmaka/store")
        .then((hanmakaLines) => {
          actionVm.$store.hanmakaLines = hanmakaLines;
          return actionVm.$store.hanmakaLines;
        });
    };

    function findHanmkaLine(conditions) {
      let key = JSON.stringify(conditions);
      for (let line of actionVm.$store.hanmakaLines) {
        if (JSON.stringify(line.conditions) == key) {
          return line;
        }
      }
      return undefined;
    }

    function isEmptyHanmkaLine(lines) {
      return (
        lines.hevraLines.pros.length == 0 &&
        lines.hevraLines.cons.length == 0 &&
        lines.mutzarLines.pros.length == 0 &&
        lines.mutzarLines.cons.length == 0
      );
    }

    function updateLine(lines) {
      let foundLines = findHanmkaLine(lines.conditions);
      if (foundLines) {
        if (isEmptyHanmkaLine(lines)) {
          let foundIndex = actionVm.$store.hanmakaLines.indexOf(foundLines);
          actionVm.$store.hanmakaLines.splice(foundIndex, 1);
        } else {
          foundLines.mutzarLines = lines.mutzarLines;
          foundLines.hevraLines = lines.hevraLines;
        }
      } else {
        actionVm.$store.hanmakaLines.push(lines);
      }
    }

    actions.saveHanmakaLines = function (multiLines) {
      return actionVm.$backend
        .postJson("/hanmaka/store/multiple", multiLines)
        .then((/* response */) => {
          for (let lines of multiLines) {
            updateLine(lines);
          }
          return multiLines;
        });
    };

    actions.removeHanmakaLines = function (lines) {
      return actionVm.$backend
        .postJSon("/hanmaka/store/", {
          conditions: lines.conditions
        })
        .then((/* response */) => {
          let foundLines = findHanmkaLine(lines.conditions);
          if (foundLines) {
            let foundIndex = actionVm.$store.hanmakaLines.indexOf(foundLines);
            actionVm.$store.hanmakaLines.splice(foundIndex, 1);
          }
          return lines;
        });
    };

    actions.explainActionInReport = function (report, polisa, group) {
      return actionVm.$backend.postJson("/hanmaka/explain-polisa", {
        report,
        polisa,
        group
      });
    };

    actions.sendEmail = function (template, title, inputs) {
      return actionVm.$backend.postJson("/notifications/send-email", {
        template,
        title,
        inputs
      });
    };

    actions.sendSms = function (number, message) {
      return actionVm.$backend.postJson("/notifications/send-sms", {
        number,
        message
      });
    };

    actions.reportBug = function ({ url, comments, screenshotBase64 }) {
      return actionVm.$backend.postJson("/notifications/report-bug", {
        url,
        comments,
        screenshotBase64
      });
    };

    actions.loadPendingNotifications = function () {
      return actionVm.$backend.getJson("/notifications/pending-notifications");
    };

    actions.notifyClientOnPublishedReport = function (clientId, reportId, notify) {
      return actionVm.$backend.postJson("/notifications/notify-published-report", {
        clientId,
        reportId,
        notify
      });
    };

    actions.sortActions = function (actions) {
      let priorityValue = {
        urgent: 6,
        important: 5,
        notice: 4,
        "non-urgent": 3,
        info: 2,
        low: 1,
        "": 0
      };
      return actions.sort((a, b) => {
        let aP = priorityValue[a.priority || ""] || 0;
        let bP = priorityValue[b.priority || ""] || 0;
        if (aP != bP) {
          return bP - aP;
        }
        if (a.endAt < b.endAt) {
          return -1;
        } else if (a.endAt > b.endA) {
          return 1;
        }
        return 0;
      });
    };

    actions.loadActions = function (query, options = {}) {
      return actionVm.$backend
        .getJson("/action", { params: query })
        .then((result) => {
          if (options.sort) {
            result = actions.sortActions(result);
          }
          return result;
        });
    };

    actions.updateAction = function (action) {
      return actionVm.$backend.postJson("/action/action", action);
    };

    actions.deleteAction = function (action) {
      return actionVm.$backend.deleteJson("/action/action/" + action.id);
    };

    actions.loadAudit = function (from, to) {
      return actionVm.$backend.getJson("/audit/events", {
        params: { from, to }
      });
    };

    actions.loadAuditSummary = function (from, to) {
      return actionVm.$backend.getJson("/audit/events/counts", {
        params: { from, to }
      });
    };

    actions.loadMessagesAudit = function (from, to) {
      return actionVm.$backend.getJson("/audit/events/messages", {
        params: { from, to }
      });
    };

    // is that been used?
    actions.loadSearchTags = function () {
      return actionVm.$backend.getJson("/json_index/tags");
    };

    // is that been used?
    actions.loadSearchTagValues = function (tag) {
      return actionVm.$backend.getJson("/json_index/tags/" + tag);
    };

    // is that been used?
    actions.loadSearchTagValuesWithFilters = function (tag, filters) {
      return actionVm.$backend.postJson("/json_index/tags/", { tag, filters });
    };

    actions.loadCompanies = function () {
      return actionVm.$backend.getJson("/hevrot");
    };

    actions.updateCompanies = function (companies) {
      return actionVm.$backend.postJson("/hevrot", companies);
    };

    actions.loadCompanyKupot = function (hevra, mutzar) {
      return actionVm.$backend.getJson(`/hevrot/${hevra}/mutzar/${mutzar}`);
    };

    actions.getHevrot = function () {
      return actionVm.$backend.getJson("/hevrot/v1");
    };

    actions.getHevraKupot = function (hevra, month) {
      return actionVm.$backend.getJson(`/hevrot/v1/${hevra}?month=${month}`);
    };

    actions.openDialog = function (setDialogState) {
      setDialogState(true);
      actionVm.$store.openDialogs.push(setDialogState);
    };

    actions.closeDialog = function () {
      if (actionVm.$store.openDialogs.length > 0) {
        let setDialogState = actionVm.$store.openDialogs.pop();
        setDialogState(false);
      }
    };

    actions.showSnackbar = function (text) {
      actionVm.$bus.$emit("showSnackbar", text);
    };

    actions.copyToClipboard = function (text, message) {
      navigator.clipboard.writeText(text).then(() => {
        if (message) {
          actionVm.$bus.$emit("showSnackbar", message);
        }
      });
    };

    actions.getMutzarOptions = function (params) {
      return actionVm.$backend.getJson("/maslul_options/mutzar", { params });
    };

    actions.getBituachOptions = function (params) {
      return actionVm.$backend.getJson("/maslul_options/bituach", { params });
    };


    actions.loadSystemInfo = function () {
      return actionVm.$backend.getJson("/audit/system");
    };

    actions.loadSystemActivity = function () {
      return actionVm.$backend.getJson("/audit/system/activity/email");
    };

    actions.loadVerifications = function () {
      return actionVm.$backend.getJson("/verifications");
    }

    actions.loadVerification = function (id) {
      return actionVm.$backend.getJson("/verifications/" + id);
    }

    actions.completeVerification = function (id, status) {
      return actionVm.$backend.postJson("/verifications/" + id, { status });
    }

    actions.archiveVerification = function (id) {
      return actionVm.$backend.postJson("/verifications/" + id + "/archive", {});
    }

    actions.loadAppBanner = function () {
      return actionVm.$backend.getJson("/content/banner");
    }

    actions.saveAppBanner = function (banner) {
      return actionVm.$backend.postJson("/content/banner", banner);
    }

    actions.loadSystemSummary = function () {
      return actionVm.$backend.getJson("/content/summary");
    }
  }
};
